import { ReactElement } from 'react';

import { Toast as BaseToast } from '@base-ui/react/toast';

import { IconProps } from '@components/icons';

type CustomButton = {
  text: string;
  icon?: ReactElement<IconProps>;
  onClick: () => void;
};

export type ToastVariant = 'info' | 'success' | 'error';

type ToastInput = {
  id?: string;
  title: string;
  subtitle?: string;
  url?: string;
  customButtons?: CustomButton[];
  duration?: number;
};

export type ToastData = {
  variant: ToastVariant;
  url?: string;
  customButtons?: CustomButton[];
  durationSeconds: number;
};

export const hasToastActions = (data: ToastData) =>
  Boolean(data.url || (data.customButtons && data.customButtons.length > 0));

const DEFAULT_DURATION_SECONDS = 5.5;

export const toastManager = BaseToast.createToastManager<ToastData>();

const showToast = (variant: ToastVariant, title: string, options: ToastInput = { title: '' }) => {
  const data: ToastData = {
    variant,
    url: options.url,
    customButtons: options.customButtons,
    durationSeconds: options.duration === 0 ? 0 : options.duration ?? DEFAULT_DURATION_SECONDS,
  };

  // Every toast auto-closes via its progress animation's animationend, which pauses
  // on hover through [data-toaster-paused]. base-ui's own timer is always disabled:
  // it doesn't reliably pause in our app — the React-17 portal plus window focus/blur
  // churn desync its internal areTimersPaused flag, so a hovered toast still expires.
  const baseTimeoutMs = 0;

  toastManager.add({
    id: options.id,
    title,
    description: options.subtitle,
    type: variant,
    timeout: baseTimeoutMs,
    data,
  });
};

type ToastOptions = Omit<ToastInput, 'title'>;

export const showSuccess = (title: string, options?: ToastOptions) =>
  showToast('success', title, { ...options, title });
export const showInfo = (title: string, options?: ToastOptions) =>
  showToast('info', title, { ...options, title });
export const showError = (title: string, options?: ToastOptions) =>
  showToast('error', title, { ...options, title });

export const remove = (id: string) => toastManager.close(id);

export const useToaster = () => {
  return { showSuccess, showError, showInfo, remove };
};
